<template>
  <div class="min-h-screen bg-gray-100 flex items-center justify-center px-4">
    <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
      <!-- Заголовок -->
      <div class="text-center mb-6">
        <svg class="mx-auto h-16 w-16 text-yellow-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <h2 class="text-2xl font-bold text-gray-900">
          {{ isMandatory ? 'Необходимо сменить пароль' : 'Смена пароля' }}
        </h2>
        <p v-if="isPasswordExpired" class="text-sm text-red-600 mt-2">
          Срок действия вашего пароля истек (более 6 месяцев). Пожалуйста, установите новый пароль.
        </p>
        <p v-else-if="isMandatory" class="text-sm text-gray-600 mt-2">
          Для обеспечения безопасности, необходимо сменить временный пароль перед началом работы.
        </p>
        <p v-else class="text-sm text-gray-600 mt-2">
          Введите текущий пароль и новый пароль для смены.
        </p>
      </div>

      <!-- Форма -->
      <form @submit.prevent="changePassword">
        <!-- Текущий пароль -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Текущий пароль
          </label>
          <input
            v-model="form.oldPassword"
            type="password"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Введите текущий пароль"
          />
        </div>

        <!-- Новый пароль -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Новый пароль
          </label>
          <input
            v-model="form.newPassword"
            type="password"
            required
            @input="validatePassword"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-500': passwordErrors.length > 0 && form.newPassword }"
            placeholder="Введите новый пароль"
          />
          
          <!-- Требования к паролю -->
          <div class="mt-2 text-xs text-gray-600">
            <p class="font-medium mb-1">Требования к паролю:</p>
            <ul class="space-y-1">
              <li v-for="(req, idx) in passwordRequirements" :key="idx" class="flex items-center">
                <svg class="w-3 h-3 mr-1" :class="isRequirementMet(idx) ? 'text-green-500' : 'text-gray-400'" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <span :class="isRequirementMet(idx) ? 'text-green-600' : 'text-gray-600'">{{ $errorMessage(req) }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Подтверждение пароля -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Подтверждение пароля
          </label>
          <input
            v-model="form.confirmPassword"
            type="password"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-500': form.confirmPassword && form.newPassword !== form.confirmPassword }"
            placeholder="Повторите новый пароль"
          />
          <p v-if="form.confirmPassword && form.newPassword !== form.confirmPassword" class="mt-1 text-xs text-red-600">
            Пароли не совпадают
          </p>
        </div>

        <!-- Ошибка -->
        <div v-if="error" class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {{ $errorMessage(error) }}
        </div>

        <!-- Успех -->
        <div v-if="success" class="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {{ success }}
        </div>

        <!-- Кнопки -->
        <div class="flex space-x-3">
          <button
            v-if="!isMandatory"
            type="button"
            @click="cancel"
            class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Отмена
          </button>
          <button
            type="submit"
            :disabled="loading || passwordErrors.length > 0 || (form.newPassword && form.newPassword !== form.confirmPassword)"
            class="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Сохранение...
            </span>
            <span v-else>Сменить пароль</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { validatePasswordComplexity, getPasswordRequirements } from '@/utils/passwordValidator'
import api from '@/services/api'

export default {
  name: 'ChangePassword',
  props: {
    isMandatory: {
      type: Boolean,
      default: false
    },
    isPasswordExpired: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      success: null,
      passwordErrors: [],
      passwordRequirements: getPasswordRequirements(),
      form: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
    }
  },
  methods: {
    validatePassword() {
      const validation = validatePasswordComplexity(this.form.newPassword)
      this.passwordErrors = validation.errors
    },

    isRequirementMet(index) {
      const password = this.form.newPassword
      if (!password) return false
      
      switch(index) {
        case 0: return password.length >= 8
        case 1: return /\d/.test(password)
        case 2: return /[A-ZА-ЯЁӘҒҚҢӨҰҮҺІ]/.test(password)
        case 3: return /[a-zа-яёәғқңөұүһі]/.test(password)
        case 4: return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)
        default: return false
      }
    },

    async changePassword() {
      this.loading = true
      this.error = null
      this.success = null

      // Валидация пароля
      const passwordValidation = validatePasswordComplexity(this.form.newPassword)
      if (!passwordValidation.isValid) {
        this.error = this.$t('passwordValidation.passwordNotMeetRequirements')
        this.loading = false
        return
      }

      // Проверка совпадения паролей
      if (this.form.newPassword !== this.form.confirmPassword) {
        this.error = this.$t('form.passwordMismatch')
        this.loading = false
        return
      }

      try {
        await api.post('/auth/change-password/', {
          old_password: this.form.oldPassword,
          new_password: this.form.newPassword,
          confirm_password: this.form.confirmPassword
        })

        this.success = 'Пароль успешно изменен. Пожалуйста, войдите с новым паролем'
        
        // Выходим из системы и перенаправляем на страницу входа
        setTimeout(async () => {
          try {
            // Выполняем выход из системы
            await api.post('/auth/logout/')
          } catch (err) {
      // Error handled silently
          } finally {
            // В любом случае перенаправляем на страницу входа
            this.$router.push('/login')
          }
        }, 1500)
      } catch (error) {
        this.error = this.$apiError(error, this.$t('errors.changePassword'))
      } finally {
        this.loading = false
      }
    },

    cancel() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
/* Дополнительные стили если нужны */
</style>

